import React from 'react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import type { OrderDetailResponse } from '../../types/api';
import { formatSide } from '../../utils/orderFormat';

interface CancelOrderConfirmationProps {
  order: OrderDetailResponse;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

/**
 * Cancel Order Confirmation component
 * Displays a confirmation dialog before canceling an order
 */
export function CancelOrderConfirmation({
  order,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: CancelOrderConfirmationProps) {
  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cancel Order?">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Are you sure you want to cancel this order?
        </p>

        {/* Order Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm font-semibold text-gray-900">
            {formatSide(order.side)} {order.quantity} {order.symbol}{' '}
            {order.order_type === 'limit' && order.limit_price && (
              <>@ Limit ${order.limit_price}</>
            )}
            {order.order_type === 'market' && '@ Market'}
          </div>
          <div className="mt-2 text-sm text-gray-600">
            <p>Client ID: <span className="font-mono">{order.client_order_id}</span></p>
            <p>Filled: {order.filled_quantity}/{order.quantity}</p>
          </div>
        </div>

        {/* Warning */}
        <div className="flex items-start bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <svg
            className="flex-shrink-0 h-5 w-5 text-yellow-400 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="ml-3 text-sm text-yellow-700">
            This action cannot be undone. Any unfilled quantity will be canceled.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Keep Order
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            isLoading={isLoading}
          >
            Cancel Order
          </Button>
        </div>
      </div>
    </Modal>
  );
}
